import React from 'react';
import cn from 'classnames';

import { DEFAULT_STYLES } from '../../constants';
import { CloseIcon } from '../../icons/CloseIcon';
import { SearchIcon } from '../../icons/SearchIcon';
import { ComboBoxTriggerFieldProps } from '../../types/comboBoxComponentTypes';

export const ComboBoxTriggerField: React.FC<ComboBoxTriggerFieldProps> = ({
  containerRef,
  inputRef,
  inputId,
  label,
  inputValue,
  placeholder,
  isMobile,
  isOpen,
  isDisabled,
  isInputFocused,
  hasError,
  showClearButton,
  inputClassName,
  isClosingRef,
  isOpeningRef,
  onInputValueChange,
  onKeyDown,
  onClear,
  onOpenChange,
  setIsInputFocused
}) => (
  <div
    ref={containerRef}
    onClick={() => {
      if (isMobile && !isDisabled && !isOpen) {
        isOpeningRef.current = true;
        onOpenChange(true);
      }
    }}
    className={cn(
      DEFAULT_STYLES.field__inputContainer,
      hasError
        ? 'border-errorRed ring-errorRed ring-opacity-50'
        : isOpen || isInputFocused
          ? 'border-lightIndigo600 ring-2 ring-lightIndigo600 ring-opacity-50 input-container-focused'
          : DEFAULT_STYLES.field__inputContainerDefault,
      isDisabled && DEFAULT_STYLES.field__inputContainerDisabled,
      isMobile && !isDisabled && 'cursor-pointer'
    )}
  >
    <div
      className="flex items-center justify-center flex-shrink-0 text-cement pointer-events-none"
      aria-hidden="true"
    >
      <SearchIcon width={16} height={16} />
    </div>

    <input
      ref={inputRef}
      id={inputId}
      type="text"
      role="combobox"
      aria-expanded={isOpen}
      aria-haspopup="listbox"
      aria-autocomplete="list"
      aria-controls={isOpen ? `${inputId}-listbox` : undefined}
      aria-labelledby={label ? `${inputId}-label` : undefined}
      aria-disabled={isDisabled}
      disabled={isDisabled}
      readOnly={isMobile}
      placeholder={placeholder}
      value={inputValue}
      onChange={(e) => onInputValueChange(e.target.value)}
      style={isDisabled ? { WebkitTextFillColor: '#94a3b8' } : undefined}
      onFocus={() => {
        setIsInputFocused(true);
        if (isMobile && !isDisabled && !isOpen) {
          isOpeningRef.current = true;
          onOpenChange(true);
          return;
        }
        if (isDisabled || isClosingRef.current) return;
        isOpeningRef.current = true;
        onOpenChange(true);
      }}
      onClick={() => {
        if (isMobile && !isDisabled && !isOpen) {
          isOpeningRef.current = true;
          onOpenChange(true);
        }
      }}
      onBlur={() => {
        if (isOpeningRef.current && isOpen) {
          requestAnimationFrame(() => {
            if (inputRef.current && isOpen && !isMobile) {
              if (document.activeElement !== inputRef.current) {
                inputRef.current.focus();
              }
            }
          });
          return;
        }
        setTimeout(() => {
          if (!isOpeningRef.current && !isOpen) {
            setIsInputFocused(false);
          }
        }, 200);
      }}
      onKeyDown={onKeyDown}
      className={cn(
        DEFAULT_STYLES.field__input,
        isDisabled
          ? DEFAULT_STYLES.field__inputDisabled
          : DEFAULT_STYLES.field__inputText,
        DEFAULT_STYLES.field__inputPlaceholder,
        isMobile && 'cursor-pointer',
        inputClassName
      )}
    />

    {showClearButton && (
      <button
        type="button"
        onClick={onClear}
        className="flex-shrink-0 mb-auto flex mt-auto text-cement hover:text-slateText transition-colors"
        tabIndex={-1}
        aria-label="Clear selection"
      >
        <CloseIcon className="w-4 h-4 mb-auto flex mt-auto" />
      </button>
    )}
  </div>
);

ComboBoxTriggerField.displayName = 'ComboBoxTriggerField';
